import { newId } from '../repositories/ids'
import { capture } from '../photos/PhotoStore'
import { makeResolveBase64 } from '../photos/resolveBlob'
import { DONE_GRACE_MS } from '../photos/purge'
import { parseMaybeJson } from '../storage/json-codec'
import { rowsOf } from '../storage/rows'
import { StorageAdapter } from '../storage/StorageAdapter'
import { answersRepository } from '../repositories/AnswersRepository'
import { addonsRepository } from '../repositories/AddonsRepository'
import { signaturesRepository } from '../repositories/SignaturesRepository'
import { occurrencesRepository } from '../repositories/OccurrencesRepository'
import { IdMappingRepository } from '../repositories/IdMappingRepository'
import { dbg } from '../logger'

/**
 * domain/occurrences.js — la FACHADA fina de la familia occurrence (MOD-01, MOD-03).
 *
 * Es la UNICA superficie que F6 (UI) importa para capturar/finalizar una occurrence offline.
 * Compone las costuras YA existentes:
 *   - los repos F2 (answers/addons/signatures/occurrences) que escriben data+outbox en UNA tx,
 *   - el PhotoStore de 06-02 (capture()) que escribe el BINARIO FUERA de SQLite y devuelve la ref.
 *
 * La fachada NO emite SQL (eso es de los repos) NI toca el transporte (eso es de los handlers del
 * drain). Cada verbo solo: (a) opcionalmente captura la foto/firma -> {image_ref, local_path},
 * (b) delega en el repo correspondiente con SOLO la referencia (el binario nunca entra a SQLite),
 * (c) devuelve el client_uuid/result del repo.
 *
 * parent_uuid del photo_meta = el client_uuid ESTABLE del padre (la answer/addon/signature) cuyo op
 * sube esa foto, para que el predicado de purga (06-02) empareje el outbox del op dueño (purge.js cruza
 * outbox.client_uuid = photo_meta.parent_uuid). LOCKED [06-02]: parent_uuid DEBE ser ese client_uuid
 * estable, NO el occurrence_id. Las TRES familias resuelven el client_uuid ANTES de capturar (addon/firma
 * lo conocen; answers lo pre-resuelve con answersRepository.stableClientUuid por (occurrence_id, item_id))
 * y lo pasan directo a capture() -> el parent_uuid nace correcto, sin reparent post-save.
 */

/**
 * Guarda/actualiza una respuesta de form, opcionalmente con foto. MOD-01 (captura).
 * Si hay photoBase64 -> PhotoStore.capture -> {image_ref, local_path}; se pasan al repo (que
 * escribe data+outbox atomico, idempotente por UNIQUE(occurrence_id,item_id)). Sin foto: refs undefined.
 * Con clearPhoto (y sin photoBase64): refs null, para quitar la foto que tuviera la respuesta.
 * @returns {Promise<string>} client_uuid estable de la respuesta.
 */
export async function saveAnswer ({
  occurrence_id: occurrenceId,
  item_id: itemId,
  value,
  current_points: currentPoints,
  total_points: totalPoints,
  extra_properties: extraProperties,
  photoBase64,
  clearPhoto = false,
  created_at: createdAt,
  updated_at: updatedAt
} = {}) {
  dbg('occ', `saveAnswer occ=${occurrenceId} item=${itemId} foto=${photoBase64 ? 'sí' : 'no'}`)
  // Pre-resolver el client_uuid ESTABLE de la answer ANTES de capturar (igual que addon/firma): así el
  // parent_uuid del photo_meta nace ya apuntando al client_uuid del op, sin un reparent post-save (que
  // si el proceso muriera entre el save y el reparent dejaría el binario sin purgar = leak).
  const answerUuid = await answersRepository.stableClientUuid(occurrenceId, itemId)
  let imageRef
  let localPath
  if (photoBase64) {
    const captured = await capture(photoBase64, {
      parentUuid: answerUuid,
      parentKind: 'answer',
      itemId
    })
    imageRef = captured.image_ref
    localPath = captured.local_path
  } else if (clearPhoto) {
    imageRef = null
    localPath = null
  }
  return answersRepository.saveAnswer({
    occurrence_id: occurrenceId,
    item_id: itemId,
    value,
    current_points: currentPoints,
    total_points: totalPoints,
    extra_properties: extraProperties,
    image_ref: imageRef,
    local_path: localPath,
    client_uuid: answerUuid,
    created_at: createdAt,
    updated_at: updatedAt
  })
}

/**
 * Guarda/actualiza un addon (latest-state, coalesce por su client_uuid). MOD-01.
 * Si es un addon de tipo imagen con photoBase64 -> captura la ref y la adjunta a la fila.
 * @returns {Promise<string>} client_uuid del addon.
 */
export async function addAddon ({
  occurrence_id: occurrenceId,
  item_id: itemId,
  type,
  answer,
  answer_ref: answerRef,
  client_uuid: clientUuid,
  photoBase64,
  fileBase64,
  filename,
  image_ref: explicitImageRef,
  local_path: explicitLocalPath
} = {}) {
  // FOTO (photoBase64) o ARCHIVO/documento (fileBase64): ambos son un binario dataURL base64. capture()
  // es genérico sobre base64 -> sirve para imágenes Y para PDFs/documentos (binario FUERA de SQLite:
  // idb web / FS nativo, ref en la fila). Antes el archivo NO tenía camino offline y se perdía.
  const binaryBase64 = photoBase64 || fileBase64
  dbg('occ', `addAddon occ=${occurrenceId} item=${itemId} type=${type} binario=${binaryBase64 ? 'sí' : 'no'}`)
  // El client_uuid del addon = client_uuid del op de outbox que sube el binario. Se resuelve ANTES de
  // capturar para sembrar parent_uuid correcto (FIX purga locked [06-02]).
  const addonUuid = clientUuid || newId()
  // Sin binario nuevo, el caller puede pasar image_ref/local_path EXPLÍCITOS (incluido null: al BORRAR
  // un addon de foto, el null limpia la ref sticky para que el drain mande el delete y no re-suba la
  // foto borrada). undefined = no tocar (sticky).
  let imageRef = explicitImageRef
  let localPath = explicitLocalPath
  if (binaryBase64) {
    const captured = await capture(binaryBase64, {
      parentUuid: addonUuid,
      parentKind: 'addon',
      itemId
    })
    imageRef = captured.image_ref
    localPath = captured.local_path
  }
  const addonRow = {
    client_uuid: addonUuid,
    occurrence_id: occurrenceId,
    item_id: itemId,
    type,
    // ARCHIVO (document con fileBase64): guardamos el FILENAME real en `answer` -> el builder lo usa como
    // nombre del multipart; el backend deriva la extensión S3 de ahí y sobrescribe answer con la URL.
    // Foto/otros: el answer que venga.
    answer: (fileBase64 && filename) ? filename : answer,
    answer_ref: answerRef,
    created_at: new Date().toISOString()
  }
  // FOTO STICKY (anti pérdida-silenciosa, mismo invariante que AnswersRepository): image_ref/local_path
  // SOLO entran al coalesce cuando se proveen. Un re-save del MISMO addon (client_uuid) sin foto NO debe
  // NULLear una foto ya guardada. Para BORRAR, el caller pasa image_ref: null explícito.
  if (imageRef !== undefined) addonRow.image_ref = imageRef
  if (localPath !== undefined) addonRow.local_path = localPath
  return addonsRepository.saveAddon(addonRow)
}

/**
 * Guarda el borrador de una occurrence (latest-state -> coalesce). MOD-01. Sin foto.
 * @returns {Promise<string>} client_uuid.
 */
export function saveDraft (occRow = {}) {
  return occurrencesRepository.saveDraft(occRow)
}

/**
 * Marca la occurrence como en-cierre y encola el EVENT de cierre. MOD-01 (finalizar). Sin foto.
 * El save-and-close multi-substep ya vive en el handler de cierre (F3).
 * @returns {Promise<string>} client_uuid.
 */
export function closeOccurrence (clientUuid, { payload, tasksEditing } = {}) {
  dbg('occ', `closeOccurrence key=${clientUuid} (FINALIZAR offline)`)
  return occurrencesRepository.markClosing(clientUuid, { payload, tasksEditing })
}

/**
 * Guarda los comentarios adicionales de la occurrence (F-97): fila local `occurrences.comments` + op
 * occurrence.saveComments en una transacción (latest-state, coalesce).
 * @returns {Promise<string>} client_uuid (id de la occurrence).
 */
export function saveComments (occurrenceId, comments) {
  dbg('occ', `saveComments occ=${occurrenceId}`)
  return occurrencesRepository.saveComments(occurrenceId, comments)
}

/**
 * Comentarios adicionales de la occurrence que todavía no llegaron al server (op occurrence.saveComments
 * pending, in_flight o dead). Lo usa el overlay de reingreso: find() reconstruye la occurrence desde el
 * server, que aún tiene el texto viejo. Incluye `dead` (como el overlay de respuestas) para no esconder un
 * texto que el usuario escribió y que no se subió. Devuelve null si no hay nada pendiente. SOLO LECTURA.
 * @returns {Promise<{ comments: string, sync_status: string }|null>}
 */
export async function getPendingComments (occurrenceId, { db = StorageAdapter } = {}) {
  if (occurrenceId == null) return null
  const res = await db.query(
    `SELECT o.comments, x.status FROM outbox x
       JOIN occurrences o ON o.client_uuid = x.client_uuid
      WHERE x.op = 'occurrence.saveComments' AND x.client_uuid = ?
        AND x.status IN ('pending','in_flight','dead')
      ORDER BY x.seq DESC LIMIT 1`,
    [String(occurrenceId)]
  )
  const row = rowsOf(res)[0]
  if (!row) return null
  return { comments: row.comments ?? '', sync_status: row.status }
}

/**
 * Marca un grupo como NA: guarda la respuesta `na` local de cada ítem (sin foto ni propiedades extra) y
 * encola la op occurrence.naGroup (na-group al drenar) en una sola transacción. El repositorio deja las
 * syncOccurrence detrás del na-group.
 * @returns {Promise<string>} client_uuid de la op.
 */
export function setNAGroup (occurrenceId, { groupId, itemIds = [] } = {}) {
  dbg('occ', `setNAGroup occ=${occurrenceId} group=${groupId} items=${itemIds.length}`)
  return StorageAdapter.transaction(async (handle) => {
    for (const itemId of itemIds) {
      await answersRepository.saveAnswerIn(handle, {
        occurrence_id: occurrenceId,
        item_id: itemId,
        value: 'na',
        current_points: 0,
        total_points: 0,
        extra_properties: [],
        image_ref: null,
        local_path: null
      })
    }
    return occurrencesRepository.enqueueNAGroupIn(handle, occurrenceId, { groupId, itemIds })
  })
}

// Boolean -> 0/1/null explícito para columnas SQLite INTEGER (ver comentario de bind-safety en
// addSignature). if/return (no ternario anidado) para que Sonar no lo vuelva a marcar.
function toNullableInt (v) {
  if (v == null) return null
  return v ? 1 : 0
}

/**
 * Captura una firma (dataURL -> PhotoStore -> signaturesRepository) y la encola offline. MOD-03.
 * Las firmas son EVENTS (no coalesce): 1-N firmas por occurrence persisten cada una.
 * @returns {Promise<string>} client_uuid de la firma.
 */
export async function addSignature ({
  occurrence_id: occurrenceId,
  form_assignment_id: formAssignmentId,
  role,
  signatureDataUrl,
  signature_kind: signatureKind,
  signed_by: signedBy,
  signed_name: signedName,
  is_auditor: isAuditor,
  is_audited: isAudited,
  audited_rol_name: auditedRolName,
  audited_name: auditedName,
  payload
} = {}) {
  dbg('occ', `addSignature occ=${occurrenceId} role=${role}`)
  // Validación al ENCOLAR (mejor diagnóstico que el 404 críptico en el drawer): sin form_assignment_id
  // el handler PUTearía a `.../form-assignments/` (URL sin id) y fallaría con 404 opaco al drenar.
  // Fallar aquí con mensaje claro, antes incluso de capturar el binario. [review Hugo]
  if (formAssignmentId == null || formAssignmentId === '') {
    throw new Error(`addSignature: form_assignment_id faltante (occ=${occurrenceId}, role=${role}); no se encola una firma sin assignment`)
  }
  // El client_uuid de la firma = client_uuid del op de outbox que sube la imagen. Se resuelve ANTES
  // de capturar para sembrar parent_uuid correcto (FIX purga locked [06-02]).
  const sigUuid = newId()
  const { image_ref: imageRef, local_path: localPath } = await capture(signatureDataUrl, {
    parentUuid: sigUuid,
    parentKind: 'signature',
    itemId: null
  })
  const sigRow = {
    client_uuid: sigUuid,
    occurrence_id: occurrenceId,
    form_assignment_id: formAssignmentId,
    role,
    image_ref: imageRef,
    local_path: localPath,
    signature_kind: signatureKind,
    // v10 (ISS-994): columnas NUEVAS que overlayPendingOfflineSignatures necesita para re-pintar la
    // firma en re-entrada (OccurrenceSignaturesSection.vue las lee directo, ver domain/occurrences.js#listPendingSignatures).
    // Booleans -> 0/1/null explícito (nunca true/false crudo): las columnas son INTEGER y bindear un
    // boolean JS es el mismo riesgo de bind-safety que motivó toText() en json-codec.js para objetos.
    signed_by: signedBy,
    signed_name: signedName,
    is_auditor: toNullableInt(isAuditor),
    is_audited: toNullableInt(isAudited),
    audited_rol_name: auditedRolName,
    audited_name: auditedName,
    created_at: new Date().toISOString()
  }
  return signaturesRepository.addSignature(sigRow, { payload })
}

/**
 * Id de servidor de una firma capturada con addSignature, cuando su op ya subió (null si aún no).
 * @param {string} clientUuid  el que devolvió addSignature.
 * @returns {Promise<number|null>}
 */
export function signatureServerId (clientUuid) {
  return IdMappingRepository.serverIdFor('signature', clientUuid)
}

/**
 * LECTURA: respuestas capturadas de una occurrence, mas recientes primero (MOD-01 read-side).
 *
 * Es la superficie de LECTURA que una vista reactiva (useReactiveQuery sobre la tabla `answers`)
 * consume — el SQL vive aqui en la capa de datos, NUNCA en el .vue (frontend-standards §5). Si
 * `occurrenceId` se omite, devuelve las respuestas mas recientes de cualquier occurrence (util para
 * el spike/playground). El binario nunca esta en SQLite: image_ref/local_path son referencias.
 *
 * @param {string} [occurrenceId]
 * @param {object} [opts]
 * @param {number} [opts.limit=50]
 * @returns {Promise<object[]>} filas de answers (objetos planos).
 */
export async function listAnswers (occurrenceId, { limit = 50 } = {}) {
  const where = occurrenceId ? 'WHERE occurrence_id=?' : ''
  const params = occurrenceId ? [occurrenceId] : []
  const sql = `SELECT client_uuid, occurrence_id, item_id, value, image_ref, updated_at
               FROM answers ${where} ORDER BY updated_at DESC LIMIT ${Number(limit)}`
  const res = await StorageAdapter.query(sql, params)
  return res?.values || []
}

/**
 * OVERLAY DE RE-ENTRADA — VALORES de respuestas (opción/puntaje) capturadas OFFLINE sin sincronizar.
 * Hermano de listPendingAnswerPhotos, pero para TODAS las respuestas pendientes: CON o SIN foto. El bug
 * que cierra: overlayPendingOfflineAnswers solo superponía las respuestas-con-foto (listPendingAnswerPhotos
 * exige image_ref), así que una respuesta de solo-opción (Sí/No/NA, sin foto) NO se superponía y al
 * reingresar se veía en BLANCO — aunque la fila sigue durable en SQLite y sincroniza (no-clobber del
 * down-sync). El usuario lo leía como "se perdieron las respuestas". Devuelve el value (los de tipo arreglo u
 * objeto, ya parseados desde su TEXT JSON) y el puntaje para superponerlos sobre el estado del server (las
 * fotos las sigue resolviendo listPendingAnswerPhotos aparte).
 */
export async function listPendingAnswers (occurrenceId, { db = StorageAdapter } = {}) {
  if (occurrenceId == null) return []
  // TKT-2356: también las respuestas cuyo op quedó en DEAD-LETTER. Antes solo pending/in_flight: cuando
  // el batch agotaba reintentos (server 4xx / 200-con-error persistente) la fila seguía durable en
  // SQLite pero DEJABA de superponerse al reingresar -> la respuesta desaparecía del formulario sin
  // aviso mientras el server tampoco la tenía. Mientras la occurrence siga abierta, lo capturado
  // localmente es la verdad del usuario; el drawer de sync permite reintentar/descartar la fila dead.
  // `sync_status` deja al overlay avisar al usuario de lo que NO llegó al server.
  const res = await db.query(
    `SELECT a.item_id, a.value, a.current_points, a.total_points, a.extra_properties,
            EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = a.client_uuid
                    AND o.status IN ('pending','in_flight')) AS is_live
       FROM answers a
      WHERE a.occurrence_id = ?
        AND EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = a.client_uuid
                    AND o.status IN ('pending','in_flight','dead'))`,
    [String(occurrenceId)]
  )
  const rows = res?.values || []
  return rows.map(r => ({
    item_id: r.item_id,
    value: parseMaybeJson(r.value),
    current_points: r.current_points,
    total_points: r.total_points,
    extra_properties: parseMaybeJson(r.extra_properties) || [],
    // 'dead' SOLO si no queda ningún op vivo para esa respuesta (si el usuario la volvió a contestar y
    // hay un op pending, el reintento en curso manda sobre la fila dead vieja).
    sync_status: Number(r.is_live) ? 'pending' : 'dead'
  }))
}

async function deadAnswerOpExists (occurrenceId, db) {
  const rows = rowsOf(await db.query(
    `SELECT 1 FROM outbox o JOIN answers a ON a.client_uuid = o.client_uuid
      WHERE a.occurrence_id = ? AND o.status = 'dead' LIMIT 1`,
    [String(occurrenceId)]
  ))
  return rows.length > 0
}

async function pendingOccurrenceSyncExists (occurrenceId, db) {
  const rows = rowsOf(await db.query(
    `SELECT body FROM outbox
      WHERE op = 'occurrence.syncOccurrence' AND status IN ('pending','in_flight')`
  )).filter(r => String(parseMaybeJson(r.body)?.occurrence_id) === String(occurrenceId))
  return rows.length > 0
}

export async function hasUnsyncedAnswers (occurrenceId, { db = StorageAdapter } = {}) {
  if (occurrenceId == null) return false
  const pendingAnswer = rowsOf(await db.query(
    `SELECT 1 FROM answers a
      WHERE a.occurrence_id = ?
        AND EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = a.client_uuid
                    AND o.status IN ('pending','in_flight')) LIMIT 1`,
    [String(occurrenceId)]
  ))
  if (pendingAnswer.length) return true
  return deadAnswerOpExists(occurrenceId, db)
}

export async function hasDeadAnswers (occurrenceId, { db = StorageAdapter } = {}) {
  if (occurrenceId == null) return false
  if (!(await deadAnswerOpExists(occurrenceId, db))) return false
  return !(await pendingOccurrenceSyncExists(occurrenceId, db))
}

/**
 * ¿La occurrence tiene un cierre local ENCOLADO pero AÚN NO drenado? = existe una fila outbox
 * op='occurrence.close' con status pending/in_flight para su occId. El client_uuid del close ==
 * occurrence.id de server (Occurrence.vue: closeOccurrence(this.occurrence.id.toString())), y find()
 * usa el MISMO id (String(data.data.id)) -> match exacto. Predicado EXACTO de la ventana
 * "finalizado-local-pero-no-drenado": tras drenar, el close pasa a status='done' -> devuelve false (sin
 * falso positivo permanente). Más preciso que occurrences.status='closing' (queda 'closing' para
 * siempre) o la meta occ-closed:<id> (ésa marca "ya aplicado en server" -> el server ya devolvería
 * is_closed=true de todos modos). Se usa en el overlay de reingreso para forzar READ-ONLY al reabrir la
 * MISMA occurrence antes de que el cierre sincronice (bug: reingreso mostraba la occ editable con datos
 * viejos porque el server aún la ve abierta). SOLO LECTURA del outbox.
 */
export async function hasPendingClose (occurrenceId, { db = StorageAdapter } = {}) {
  if (occurrenceId == null) return false
  const res = await db.query(
    `SELECT 1 FROM outbox
      WHERE op = 'occurrence.close' AND client_uuid = ?
        AND status IN ('pending','in_flight') LIMIT 1`,
    [String(occurrenceId)]
  )
  return !!res?.values?.length
}

/**
 * ¿El SERVER puede NO tener aplicado el cierre de esta occurrence? Mismo predicado que
 * `hasPendingClose` pero incluyendo tambien los cierres `dead` (agotaron reintentos). La diferencia
 * importa segun para que se pregunte:
 *
 *   - `hasPendingClose` (overlay de reingreso): un cierre `dead` NO debe forzar read-only para
 *     siempre — la occurrence vuelve a ser editable para poder re-finalizarla.
 *   - `hasUnconfirmedClose` (correo/PDF): un cierre `dead` significa que el server sigue viendo la
 *     auditoria ABIERTA, asi que pedirle el documento produciria el PDF equivocado ("Sin Finalizar",
 *     calificacion 0) — exactamente el sintoma de TKT-2182, solo que por otra via.
 *
 * SOLO LECTURA del outbox.
 */
export async function hasUnconfirmedClose (occurrenceId, { db = StorageAdapter } = {}) {
  if (occurrenceId == null) return false
  const res = await db.query(
    `SELECT 1 FROM outbox
      WHERE op = 'occurrence.close' AND client_uuid = ?
        AND status IN ('pending','in_flight','dead') LIMIT 1`,
    [String(occurrenceId)]
  )
  return !!res?.values?.length
}

/**
 * ¿El cierre de esta occurrence quedo en dead-letter? Un cierre `dead` NO se recupera solo (el drain
 * ya no lo toma), asi que quien este esperando a que llegue al server puede dejar de esperar en vez
 * de agotar su presupuesto. El drawer de dead-letter es la via de recuperacion. SOLO LECTURA.
 */
export async function hasDeadClose (occurrenceId, { db = StorageAdapter } = {}) {
  if (occurrenceId == null) return false
  const res = await db.query(
    "SELECT 1 FROM outbox WHERE op = 'occurrence.close' AND client_uuid = ? AND status = 'dead' LIMIT 1",
    [String(occurrenceId)]
  )
  return !!res?.values?.length
}

/**
 * Score del cierre local pendiente. Complemento de `hasPendingClose`: mismo predicado exacto de
 * outbox (op='occurrence.close', status pending/in_flight, client_uuid=occId), pero en vez de un
 * boolean devuelve el `score` que quedó persistido en la fila local `occurrences` al encolar el
 * cierre (ver OccurrencesRepository.markClosing). Se usa para RESTAURAR el score visible en el
 * overlay de reingreso: is_closed se fuerza a true mientras el cierre no drena, pero sin esto
 * currentScore (getters.js) leía el score VIEJO del server (aún sin cerrar) -> undefined/vacío.
 * Devuelve null si no hay cierre pendiente para esa occurrence (mismo criterio que hasPendingClose)
 * o si la fila local no tiene score persistido — NUNCA lanza, SOLO LECTURA.
 * @param {string} occurrenceId
 * @param {{ db?: object }} deps
 * @returns {Promise<number|null>}
 */
export async function getPendingCloseScore (occurrenceId, { db = StorageAdapter } = {}) {
  if (occurrenceId == null) return null
  const res = await db.query(
    `SELECT o.score FROM occurrences o
      WHERE o.client_uuid = ?
        AND EXISTS (
          SELECT 1 FROM outbox ob
           WHERE ob.op = 'occurrence.close' AND ob.client_uuid = o.client_uuid
             AND ob.status IN ('pending','in_flight')
        )
      LIMIT 1`,
    [String(occurrenceId)]
  )
  const row = res?.values?.[0]
  return row?.score ?? null
}

/**
 * OVERLAY DE RE-ENTRADA (MOD-01): ANSWERS-foto (item-foto) capturadas OFFLINE que AÚN no se subieron
 * (su op de outbox sigue pending/in_flight). `find()` reconstruye this.current desde el SERVER y NO las
 * trae -> al reabrir la occurrence la foto quedaba INVISIBLE (`v-if="answer"` falso -> el ítem vuelve a
 * mostrar el botón de cámara virgen), aunque la fila/binario siguen durables en SQLite: el usuario lo
 * lee como "se perdió la foto" y la re-toma. Esta lectura las devuelve con su binario resuelto a base64
 * (dataURL) para que el store las SUPERPONGA sobre el estado del server.
 *
 * Es la MITAD que faltaba del invariante que ya cumplían los addons (listPendingAddonPhotos): lo
 * mostrado = server SUPERPUESTO con las escrituras offline pendientes. Solo se había implementado para
 * `addons`, no para `answers`.
 *
 * LWW: se superpone mientras el op siga pending/in_flight, Y TAMBIÉN con op `done` mientras el binario
 * local siga vivo (photo_meta.upload_status != 'done'; la purga lo flipa exactamente al borrar el
 * binario). El `done` es NECESARIO por la CARRERA DE REINGRESO (reproducida en
 * ui-photo-reentry-race.smoke.mjs): el drain arranca con el boot y una op que flipa a done DURANTE el
 * vuelo del find() no está NI en el snapshot del server NI en un overlay solo-pending -> la foto
 * desaparecía visualmente hasta el siguiente reingreso. Con done+binario-vivo se pinta desde el binario
 * local; cuando la purga lo barre (≥DONE_GRACE_MS tras el done, ver purge.js), cualquier find()
 * posterior ya la trae del server. Sin duplicados: setAddons/setCurrentOccurrenceAnswer coalescen por
 * slot/item, y el guard de photo_meta evita además lecturas muertas de binarios ya purgados.
 *
 * SOLO LECTURA (no toca outbox ni el binario). Best-effort: si un binario no resuelve (evictado), se
 * omite (no rompe la carga). SQL vía el `db` inyectado (default = StorageAdapter, STOR-05); el binario
 * se lee por el backend activo (web idb / FS nativo) vía resolveBase64 inyectable (gates).
 *
 * @param {string} occurrenceId
 * @param {{ db?: object, resolveBase64?: Function }} deps
 * @returns {Promise<Array<{ item_id:number, value:string, current_points:number, total_points:number,
 *   extra_properties:* }>>}
 */
export async function listPendingAnswerPhotos (
  occurrenceId,
  { db = StorageAdapter, resolveBase64 = makeResolveBase64() } = {}
) {
  if (occurrenceId == null) return []
  const res = await db.query(
    `SELECT a.item_id, a.image_ref, a.local_path, a.current_points, a.total_points, a.extra_properties
       FROM answers a
      WHERE a.occurrence_id = ? AND a.image_ref IS NOT NULL
        AND EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = a.client_uuid
                    AND o.status IN ('pending','in_flight','done','dead'))
        AND EXISTS (SELECT 1 FROM photo_meta pm WHERE pm.image_ref = a.image_ref
                    AND pm.upload_status != 'done')`,
    [String(occurrenceId)]
  )
  const rows = res?.values || []
  const out = []
  for (const r of rows) {
    // NO se devuelve `local_path`: aquí es una ruta RELATIVA del sandbox nativo (NativeFsBackend), y el
    // `localPath` del store es un URI `file://` (saveItemAnswer). Mezclarlos metería una ruta relativa en
    // un campo que la UI puede usar como src de <img> (LightboxPicture). El base64 basta para pintar y
    // editar.
    const value = await resolveBase64({ image_ref: r.image_ref, local_path: r.local_path })
    if (value) {
      out.push({
        item_id: r.item_id,
        value,
        current_points: r.current_points,
        total_points: r.total_points,
        extra_properties: parseMaybeJson(r.extra_properties) || []
      })
    }
  }
  return out
}

/**
 * Igual que listPendingAnswerPhotos pero para los ADDONS-foto. Ver la nota de arriba: son las dos
 * mitades del MISMO invariante (lo mostrado = server + escrituras offline pendientes).
 *
 * EXCLUYE type='document' A PROPÓSITO: los documentos también tienen image_ref (capture es genérico,
 * los PDFs pasan por PhotoStore), pero su `answer` es el FILENAME (addAddon:125), no una imagen. Este
 * overlay REEMPLAZA answer con el binario base64 — para una foto eso es pintarla; para un documento es
 * PISARLE EL NOMBRE con un data-URI de varios MB: FieldUploadFile construye nombre Y url desde answer
 * (getAnswer:75-77) -> el chip mostraba "data:image/jpeg;base…" y el click dejaba de abrir. El pisado
 * era intermitente (el overlay moría por la carrera de this.current) y el fix de la carrera lo volvió
 * consistente — por eso "reapareció" un bug de documentos ya resuelto en FieldUploadFile.
 * Trade-off aceptado: un documento pendiente NO se re-superpone al reingresar tras matar la app
 * (gap pre-overlay de siempre para documentos; el dato sigue durable y sube con el drain).
 *
 * @param {string} occurrenceId
 * @param {{ db?: object, resolveBase64?: Function }} deps
 * @returns {Promise<Array<{ item_id:number, type:string, answer_ref:string, image_ref:string, local_path:string, answer:string }>>}
 */
// TKT-2356 (review Victayy): los overlays de ADICIÓN de addons incluyen tambien 'dead'. Son la misma
// mitad del invariante que listPendingAnswerPhotos: si el op del outbox agota reintentos, el addon-foto o
// el comentario siguen durables en SQLite pero dejaban de superponerse al reingresar -> desaparecian del
// formulario sin aviso (mismo sintoma del ticket, por el camino de addons). Los overlays que ELIMINAN
// (listPendingDeletedAddons) NO lo incluyen a proposito: ahi el sesgo seguro es no ocultar de mas.
export async function listPendingAddonPhotos (
  occurrenceId,
  { db = StorageAdapter, resolveBase64 = makeResolveBase64() } = {}
) {
  if (occurrenceId == null) return []
  const res = await db.query(
    `SELECT a.item_id, a.type, a.answer_ref, a.image_ref, a.local_path
       FROM addons a
      WHERE a.occurrence_id = ? AND a.image_ref IS NOT NULL
        AND a.type != 'document'
        AND EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = a.client_uuid
                    AND o.status IN ('pending','in_flight','done','dead'))
        AND EXISTS (SELECT 1 FROM photo_meta pm WHERE pm.image_ref = a.image_ref
                    AND pm.upload_status != 'done')`,
    [String(occurrenceId)]
  )
  const rows = res?.values || []
  const out = []
  for (const r of rows) {
    const answer = await resolveBase64({ image_ref: r.image_ref, local_path: r.local_path })
    if (answer) out.push({ ...r, answer })
  }
  return out
}

// Un comentario ya sincronizado ('done') solo se re-pinta si terminó hace menos de DONE_GRACE_MS.
export async function listPendingTextAddons (
  occurrenceId,
  { db = StorageAdapter, now = Date.now() } = {}
) {
  if (occurrenceId == null) return []
  const res = await db.query(
    `SELECT a.item_id, a.type, a.answer
       FROM addons a
      WHERE a.occurrence_id = ? AND a.image_ref IS NULL
        AND a.type = 'comment'
        AND a.answer IS NOT NULL AND a.answer != ''
        AND EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = a.client_uuid
                    AND (o.status IN ('pending','in_flight','dead')
                         OR (o.status = 'done' AND COALESCE(o.done_at, 0) >= ?)))`,
    [String(occurrenceId), now - DONE_GRACE_MS]
  )
  return res?.values || []
}

/**
 * OVERLAY DE RE-ENTRADA — BORRADOS pendientes. La otra mitad del invariante: listPendingAddonPhotos/
 * listPendingTextAddons RE-PINTAN lo AGREGADO offline; este devuelve lo BORRADO offline que el server
 * TODAVÍA trae. find() reconstruye this.current desde el SERVER; si el borrado aún no drenó (o el server
 * lo conservó), el addon reaparecía al reabrir y quedaba pegado en la vista, sin poder abrirlo (bug
 * visual TKT-2280). El caller QUITA estos slots de this.current.addons.
 *
 * Borrado = fila local con answer vacío/null Y sin binario (image_ref NULL), con op en outbox SIN drenar
 * (pending/in_flight). Se EXCLUYE 'done' a propósito: una vez sincronizado, el server es autoritativo —
 * si aún devuelve el addon es porque el borrado NO tomó efecto (otro dispositivo lo re-agregó, o el gate
 * de versión rechazó el delete), y ocultarlo mentiría al usuario hasta que el op se pode (7 días). A
 * diferencia de los overlays de ADICIÓN (que re-pintan y un dato viejo es inocuo), este ELIMINA, así que
 * el sesgo seguro es NO ocultar de más. Coalesce por client_uuid: si el addon se RE-agregó localmente,
 * su fila vuelve a tener contenido y deja de calificar.
 */
export async function listPendingDeletedAddons (
  occurrenceId,
  { db = StorageAdapter } = {}
) {
  if (occurrenceId == null) return []
  const res = await db.query(
    `SELECT a.item_id, a.type
       FROM addons a
      WHERE a.occurrence_id = ? AND a.image_ref IS NULL
        AND (a.answer IS NULL OR a.answer = '')
        AND EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = a.client_uuid
                    AND o.status IN ('pending','in_flight'))`,
    [String(occurrenceId)]
  )
  return res?.values || []
}

/**
 * OVERLAY DE RE-ENTRADA (ISS-994): FIRMAS capturadas OFFLINE que AÚN no se subieron. Última pieza
 * del invariante que ya cumplían addons/answers: `find()` reconstruye this.current desde el SERVER y
 * NO trae las firmas capturadas offline -> al reabrir la occurrence tras matar la app desaparecían
 * visualmente hasta que el outbox drenaba (aunque la fila/binario siguen durables en SQLite: el
 * usuario lo lee como "se perdió la firma"). Devuelve las firmas pendientes con su binario resuelto a
 * base64 (dataURL) más las columnas de display (signed_by/signed_name/is_auditor/is_audited/
 * audited_rol_name/audited_name, ver migración v10) para que el store las SUPERPONGA como entradas
 * NUEVAS en el array de firmas (a diferencia de addons/answers, las firmas NO se coalescen por
 * item/slot: son EVENTS append-only, el dedupe entre reingresos lo hace el caller por client_uuid).
 *
 * OUTBOX pending/in_flight SOLAMENTE — 'done' se EXCLUYE a propósito (a diferencia de
 * listPendingAddonPhotos/AnswerPhotos, que sí lo incluyen). Las firmas son EVENTS append-only y su
 * dedup (por _pendingClientUuid en el caller) NO reconoce las firmas que YA vienen del server en el
 * find() (esas no llevan esa marca). Con 'done' incluido, una firma ya subida la trae el server Y el
 * overlay la vuelve a empujar -> quedan DUPLICADAS al reingresar (bug reproducido en móvil: 4 firmas
 * -> salir -> volver -> 8; en la BD sólo hay 4). Fotos/comentarios no sufren esto porque COALESCEN por
 * slot (setAddons pisa la misma entrada); las firmas no. Restringir a pending/in_flight cierra la
 * duplicación: una firma 'done' ya está en el server (su PUT respondió 2xx en saveSignature) -> la
 * sirve el find(), el overlay ya no la re-pinta.
 *
 * Trade-off aceptado (mismo criterio que listPendingDeletedAddons): se pierde la cobertura de la
 * carrera de reingreso que 'done' daba (un find() EN VUELO justo cuando el drain marca 'done' podría
 * traer un snapshot del server sin la firma -> parpadeo transitorio hasta el próximo find()). Esa
 * ventana es de milisegundos y auto-corrige; la duplicación persistente que causaba 'done' es peor.
 * A diferencia de fotos, collapse-sibling-sync NO toca ops de firma (solo occurrence.syncOccurrence),
 * así que un 'done' en una firma SIEMPRE es subida real -> no hay falso 'done' que compense.
 *
 * SOLO LECTURA (no toca outbox ni el binario). Best-effort: si un binario no resuelve (evictado), se
 * omite (no rompe la carga).
 *
 * @param {string} occurrenceId
 * @param {{ db?: object, resolveBase64?: Function }} deps
 * @returns {Promise<Array<{ client_uuid:string, signature_path:string, role:string, signature_kind:string,
 *   signed_by:number, signed_name:string, is_auditor:number, is_audited:number,
 *   audited_rol_name:string, audited_name:string }>>}
 */
export async function listPendingSignatures (
  occurrenceId,
  { db = StorageAdapter, resolveBase64 = makeResolveBase64() } = {}
) {
  if (occurrenceId == null) return []
  const res = await db.query(
    `SELECT s.client_uuid, s.image_ref, s.local_path, s.role, s.signature_kind,
            s.signed_by, s.signed_name, s.is_auditor, s.is_audited,
            s.audited_rol_name, s.audited_name
       FROM signatures s
      WHERE s.occurrence_id = ? AND s.image_ref IS NOT NULL
        AND EXISTS (SELECT 1 FROM outbox o WHERE o.client_uuid = s.client_uuid
                    AND o.status IN ('pending','in_flight'))
        AND EXISTS (SELECT 1 FROM photo_meta pm WHERE pm.image_ref = s.image_ref
                    AND pm.upload_status != 'done')`,
    [String(occurrenceId)]
  )
  const rows = res?.values || []
  const out = []
  for (const r of rows) {
    const signature_path = await resolveBase64({ image_ref: r.image_ref, local_path: r.local_path })
    if (signature_path) {
      out.push({
        client_uuid: r.client_uuid,
        signature_path,
        role: r.role,
        signature_kind: r.signature_kind,
        signed_by: r.signed_by,
        signed_name: r.signed_name,
        is_auditor: r.is_auditor,
        is_audited: r.is_audited,
        audited_rol_name: r.audited_rol_name,
        audited_name: r.audited_name
      })
    }
  }
  return out
}

/**
 * Lista los addons OFFLINE de una occurrence (opcionalmente de un ítem). Lectura para que el UI
 * pueda saber qué slots de foto (picture/second_picture/...) ya están OCUPADOS en SQLite aunque el
 * estado en memoria se haya reconstruido (re-entrada: `find()` trae solo el estado del server, sin
 * las capturas offline pendientes). Sin esto, el slot-finder de addImage reusaba un slot ocupado ->
 * el client_uuid determinista coalescea -> la foto pendiente de ese slot se PISABA (pérdida).
 * @param {string} occurrenceId
 * @param {number|string} [itemId]  filtra por ítem.
 * @returns {Promise<object[]>} filas de addons (objetos planos).
 */
export async function listAddons (occurrenceId, itemId) {
  if (occurrenceId == null) return []
  let where = 'WHERE occurrence_id=?'
  const params = [String(occurrenceId)]
  if (itemId != null) {
    where += ' AND item_id=?'
    params.push(itemId)
  }
  const res = await StorageAdapter.query(
    `SELECT client_uuid, occurrence_id, item_id, type, answer, answer_ref, image_ref FROM addons ${where}`,
    params
  )
  return res?.values || []
}

/**
 * Borra el ESPEJO LOCAL de una occurrence (answers + addons). Lo usa el "borrar todas las
 * respuestas" del formulario, que sólo borraba en el SERVIDOR: las filas locales sobrevivían y
 * `find()` las volvía a superponer, así que las respuestas-foto REAPARECÍAN. No toca el outbox:
 * un op que drene después no encuentra filas que enviar.
 * @param {string|number} occurrenceId
 */
export async function clearLocalAnswers (occurrenceId, { db = StorageAdapter } = {}) {
  await db.run('DELETE FROM answers WHERE occurrence_id = ?', [occurrenceId])
  await db.run('DELETE FROM addons WHERE occurrence_id = ?', [occurrenceId])
}
